/**
 * Tiny dependency-free confetti + haptics for the satisfying "check it off" moment.
 *
 * One shared full-screen canvas, a single rAF loop that runs only while particles are alive.
 * Honours prefers-reduced-motion (no-ops) so it never becomes a nuisance.
 */

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  size: number;
  color: string;
  life: number;
  maxLife: number;
}

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let particles: Particle[] = [];
let raf = 0;
let last = 0;

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function ensureCanvas(): void {
  if (canvas) return;
  canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:90';
  document.body.appendChild(canvas);
  ctx = canvas.getContext('2d');
  resize();
  window.addEventListener('resize', resize);
}

function resize(): void {
  if (!canvas || !ctx) return;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function tick(now: number): void {
  if (!ctx || !canvas) return;
  const dt = Math.min(48, now - last) / 1000;
  last = now;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  particles = particles.filter((p) => p.life > 0);
  for (const p of particles) {
    p.life -= dt;
    p.vy += 900 * dt; // gravity
    p.vx *= 0.99;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.rot += p.vr * dt;

    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, p.life / (p.maxLife * 0.6)));
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.7);
    ctx.restore();
  }

  if (particles.length > 0) {
    raf = requestAnimationFrame(tick);
  } else {
    raf = 0;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }
}

const DEFAULT_COLORS = ['#d4a04a', '#4caf7d', '#5b9cf6', '#c586c0', '#e0764f'];

function spawn(x: number, y: number, count: number, colors: string[], power: number): void {
  if (reducedMotion()) return;
  ensureCanvas();
  const palette = colors.length ? colors : DEFAULT_COLORS;
  for (let i = 0; i < count; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
    const speed = power * (0.5 + Math.random());
    particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 40,
      vy: Math.sin(angle) * speed,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 12,
      size: 5 + Math.random() * 5,
      color: palette[Math.floor(Math.random() * palette.length)]!,
      life: 0.8 + Math.random() * 0.5,
      maxLife: 1.3,
    });
  }
  if (!raf) {
    last = performance.now();
    raf = requestAnimationFrame(tick);
  }
}

/** A small burst at a point — for a single checkbox. */
export function confettiBurst(x: number, y: number, color?: string): void {
  const colors = color ? [color, ...DEFAULT_COLORS.slice(0, 2)] : DEFAULT_COLORS;
  spawn(x, y, 14, colors, 340);
}

/** A big celebration from the bottom-centre — for finishing a whole list. */
export function confettiBig(): void {
  if (reducedMotion()) return;
  const y = window.innerHeight;
  spawn(window.innerWidth * 0.5, y, 80, DEFAULT_COLORS, 900);
  spawn(window.innerWidth * 0.25, y, 40, DEFAULT_COLORS, 820);
  spawn(window.innerWidth * 0.75, y, 40, DEFAULT_COLORS, 820);
}

/** A short buzz on devices that support it (Android/Chrome; iOS Safari ignores it). */
export function haptic(pattern: number | number[] = 12): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* not supported */
  }
}
